'use client';

import { useEffect, useState } from 'react';

import { Skeleton, Typography } from 'lighthouse';

import { countActiveAttributeFilters } from '@/utilities/attributeFilters';
import { ATTRIBUTE_FILTER_COPY, BRAND_FILTER_SLUG } from '@/utilities/attributeFilters/constants';
import { AttributeFiltersSelection } from '@/utilities/attributeFilters/models';

import { AttributeFiltersPanelProps } from '../../models';
import { ActiveAttributeFilterChips } from '../ActiveAttributeFilterChips';
import { AttributeBooleanFilter } from '../AttributeBooleanFilter';
import { AttributeBrandFilter } from '../AttributeBrandFilter';
import { AttributeFilterAccordion } from '../AttributeFilterAccordion';
import { AttributeFilterTitle } from '../AttributeFilterTitle';
import { AttributeRangeFilter } from '../AttributeRangeFilter';
import { AttributeValuesFilter } from '../AttributeValuesFilter';

import { CategoryFilterAttribute } from '@/server/services/core/category/models';
import { Filter } from 'iconsax-react';

const hasAttributeSelection = (attribute: CategoryFilterAttribute, selection: AttributeFiltersSelection) => {
    if (attribute.filterMode === 'RANGE') {
        const range = selection.ranges[attribute.slug];
        return Boolean(range?.after || range?.before);
    }

    return Boolean(selection.values[attribute.slug]?.length);
};

const getInitialExpandedSlug = (schema: CategoryFilterAttribute[], selection: AttributeFiltersSelection, hasBrandSelection: boolean) => {
    const selectedAttribute = schema.find(
        (attribute) => !(attribute.filterMode === 'VALUES' && attribute.type === 'BOOLEAN') && hasAttributeSelection(attribute, selection)
    );

    return selectedAttribute?.slug ?? (hasBrandSelection ? BRAND_FILTER_SLUG : null);
};

export const AttributeFiltersPanel = ({
    schema,
    brandFilter,
    selection,
    isLoading,
    draft,
    hasInvalidRanges,
    onSetValues,
    onSetRange,
    onClearAll
}: AttributeFiltersPanelProps) => {
    const hasBrandSelection = Boolean((draft ? brandFilter?.draftSelectedIds : brandFilter?.selectedIds)?.length);
    const [expandedSlug, setExpandedSlug] = useState<string | null>(() => getInitialExpandedSlug(schema, selection, hasBrandSelection));

    useEffect(() => {
        if (expandedSlug != null || isLoading) {
            return;
        }

        const nextExpanded = getInitialExpandedSlug(schema, selection, hasBrandSelection);
        if (nextExpanded) {
            setExpandedSlug(nextExpanded);
        }
    }, [expandedSlug, hasBrandSelection, isLoading, schema, selection]);

    if (isLoading) {
        return (
            <div className='space-y-3 rounded-lg border border-solid border-N100 bg-white p-3'>
                <Skeleton variant='rounded' width='40%' height={20} />
                <Skeleton variant='rounded' width='100%' height={72} />
                <Skeleton variant='rounded' width='100%' height={72} />
            </div>
        );
    }

    if (!schema.length && !brandFilter) {
        return null;
    }

    const brandSelectedCount = (draft ? brandFilter?.draftSelectedIds : brandFilter?.selectedIds)?.length ?? 0;
    const activeCount = countActiveAttributeFilters(selection) + brandSelectedCount;

    return (
        <div className={draft ? 'space-y-3' : 'space-y-3 rounded-lg border border-solid border-N100 bg-white p-3'}>
            <div className='flex items-center justify-between gap-2'>
                {!draft ? <AttributeFilterTitle name={ATTRIBUTE_FILTER_COPY.panelTitle} icon={Filter} /> : null}
                <ActiveAttributeFilterChips activeCount={activeCount} onClearAll={() => onClearAll(draft ? { draft: true } : undefined)} />
            </div>

            {brandFilter ? (
                <AttributeFilterAccordion
                    name={ATTRIBUTE_FILTER_COPY.brandTitle}
                    selectedCount={brandSelectedCount}
                    expanded={expandedSlug === BRAND_FILTER_SLUG}
                    onExpandedChange={(nextExpanded) => setExpandedSlug(nextExpanded ? BRAND_FILTER_SLUG : null)}>
                    <AttributeBrandFilter config={brandFilter} draft={draft} />
                </AttributeFilterAccordion>
            ) : null}

            {schema.map((attribute) => {
                if (attribute.filterMode === 'VALUES' && attribute.type === 'BOOLEAN') {
                    return (
                        <AttributeBooleanFilter
                            key={attribute.slug}
                            attribute={attribute}
                            selection={selection}
                            draft={draft}
                            onSetValues={onSetValues}
                        />
                    );
                }

                return (
                    <AttributeFilterAccordion
                        key={attribute.slug}
                        name={attribute.name}
                        selectedCount={attribute.filterMode === 'RANGE' ? 0 : (selection.values[attribute.slug]?.length ?? 0)}
                        expanded={expandedSlug === attribute.slug}
                        onExpandedChange={(nextExpanded) => setExpandedSlug(nextExpanded ? attribute.slug : null)}>
                        {attribute.filterMode === 'RANGE' ? (
                            <AttributeRangeFilter attribute={attribute} selection={selection} draft={draft} onSetRange={onSetRange} />
                        ) : (
                            <AttributeValuesFilter attribute={attribute} selection={selection} draft={draft} onSetValues={onSetValues} />
                        )}
                    </AttributeFilterAccordion>
                );
            })}

            {hasInvalidRanges && (
                <Typography variant='caption' color='error' component='p'>
                    {ATTRIBUTE_FILTER_COPY.invalidRange}
                </Typography>
            )}
        </div>
    );
};
